import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { ShopLogo } from "@/components/brand/ShopLogo";
import { BrowseIntro } from "@/components/browse/BrowseIntro";
import { BrowseLocationPills } from "@/components/browse/BrowseLocationPills";
import { BrowseSortControl } from "@/components/browse/BrowseSortControl";
import { BrowseViewToggle } from "@/components/browse/BrowseViewToggle";
import { PricesAsOfLine, PricesUpdatedOverlay } from "@/components/browse/PricesFreshness";
import { PublicBrowseSkeleton } from "@/components/browse/PublicBrowseSkeleton";
import { PublicGridCard } from "@/components/browse/PublicGridCard";
import { PublicListRow } from "@/components/browse/PublicListRow";
import {
  StorefrontListShell,
  StorefrontLoadError,
  StorefrontToolbar,
} from "@/components/browse/StorefrontListShell";
import { TableSwitcher } from "@/components/browse/TableSwitcher";
import { WholeShopLink } from "@/components/browse/WholeShopLink";
import { PublicBuylistCardRow } from "@/components/buylist/PublicBuylistCardRow";
import { PublicBuylistRules } from "@/components/buylist/PublicBuylistRules";
import { AppliedFilterChips } from "@/components/filters/AppliedFilterChips";
import { PullSheetBar } from "@/components/pull-sheet/PullSheetBar";
import { PullSheetErrorToast } from "@/components/pull-sheet/PullSheetErrorToast";
import { PullSheetReviewModal } from "@/components/pull-sheet/PullSheetReviewModal";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FlashList, type ListRef } from "@/components/ui/FlashList";
import { useBrowseIntroDismissed } from "@/hooks/useBrowseIntroDismissed";
import { useCollapsingHeader } from "@/hooks/useCollapsingHeader";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import {
  type PublicBrowseItem,
  usePublicBrowse,
  usePublicBrowseGames,
} from "@/hooks/usePublicBrowse";
import { usePublicBrowseView } from "@/hooks/usePublicBrowseView";
import { usePullSheet } from "@/hooks/usePullSheet";
import { useStorefrontFilters } from "@/hooks/useStorefrontFilters";
import { useStorefrontGrid } from "@/hooks/useStorefrontGrid";
import { useTableBuylist } from "@/hooks/useTableBuylist";
import {
  type BrowseFilters,
  type BrowseSort,
  filterAndSortBrowse,
  hasBrowseFilters,
  hasPriceBounds,
  needsWholeTable,
  NO_REFINEMENTS,
  refinementFilters,
  refinementParams,
  refinementsFromParams,
} from "@/lib/browse/browseFilter";
import { loadShopAnalytics } from "@/lib/browse/shopAnalyticsClient";
import { currentBrowseArrival, loadShowAnalytics } from "@/lib/browse/showAnalyticsClient";
import { storefrontFacets } from "@/lib/browse/storefrontFacets";
import { gridGutters } from "@/lib/inventory/desktopGrid";
import { shopPath, tablePath } from "@/lib/shop/publicUrls";

/**
 * The Pull Sheet customer browse page (#340, #341, #342; ADR-0017, ADR-0014). A table QR scan
 * opens `/browse/<event public_id>` and shows a vendor's live inventory on the customer's phone
 * with no login.
 *
 * The customer controls a persisted Grid/List toggle (`usePublicBrowseView`, List by default), can
 * open item detail (`/browse/<eventId>/<itemId>`, with comps), and picks items. `PublicGridCard`
 * and `PublicListRow` follow `StockGridCard` and `StockRow` visually but use the narrower
 * `PublicBrowseItem` schema (no `cost_basis`). `usePullSheet` owns picking:
 * device-local resumability (`event_public_id` to `pull_sheet_id`), the running count and
 * subtotal, and pick, unpick and present.
 *
 * The route is a sibling of `(app)`, so `ClerkProvider` and `AuthRedirectGate` never mount around
 * it (see `app/_layout.tsx`). Neither `usePublicBrowse` nor `usePullSheet` sends an auth header, so
 * signed-in staff see exactly what an anonymous customer sees.
 */
export default function BrowseScreen() {
  const params = useLocalSearchParams<{
    eventId: string;
    q?: string;
    game?: string;
    set?: string;
    setGame?: string;
    sort?: string;
    location?: string;
    kind?: string;
    graded?: string;
    cond?: string;
    type?: string;
    min?: string;
    max?: string;
    grader?: string;
    grade?: string;
  }>();
  const { eventId } = params;
  // A repeated query param (`?q=a&q=b`) arrives as a string[] at runtime despite the generic. Take
  // the first value so a malformed URL degrades to an ordinary filter instead of throwing.
  const one = (value: string | string[] | undefined): string | undefined =>
    Array.isArray(value) ? value[0] : value;
  const sortParam = one(params.sort);
  const initialSort: BrowseSort = sortParam === "price-asc" || sortParam === "price-desc"
      || sortParam === "name"
    ? sortParam
    : "featured";
  const [sort, setSort] = useState<BrowseSort>(initialSort);
  // The set facet's game has its own `setGame` param because clearing the game filter keeps the set
  // selected, so the two must survive Back independently. A bare `game` still seeds it for a
  // hand-written link.
  const setName = one(params.set);
  const setGame = one(params.setGame) ?? one(params.game);
  // Every facet, the Location pill and the sheet's other refinements (TRO-1095) included, is kept in
  // the URL, since the detail route may remount this page on browser Back. `setParams` replaces the
  // current history entry, so edits add no Back stops.
  const storefrontFilters = useStorefrontFilters(
    {
      game: one(params.game) ?? null,
      set: setName && setGame ? { game: setGame, name: setName } : null,
      stockGroup: one(params.location) ?? null,
      refinements: refinementsFromParams({
        kind: one(params.kind),
        graded: one(params.graded),
        cond: one(params.cond),
        type: one(params.type),
        min: one(params.min),
        max: one(params.max),
        grader: one(params.grader),
        grade: one(params.grade),
      }),
    },
    (changed) => {
      if ("game" in changed) router.setParams({ game: changed.game ?? undefined });
      if ("set" in changed) {
        router.setParams(
          changed.set
            ? { set: changed.set.name, setGame: changed.set.game }
            : { set: undefined, setGame: undefined },
        );
      }
      if ("stockGroup" in changed) router.setParams({ location: changed.stockGroup ?? undefined });
      if (changed.refinements) router.setParams(refinementParams(changed.refinements));
    },
  );
  const { game: gameSel, set: setSel, stockGroup, refinements, applyFacetPatch } =
    storefrontFilters;
  // Search runs on the server and sort is applied here (#468, #768). The search is debounced so a
  // typed word is one request; the box stays responsive on `query`.
  const [query, setQuery] = useState(one(params.q) ?? "");
  const search = useDebouncedValue(query, 250);
  const chosen = useMemo<BrowseFilters>(
    () => ({ game: gameSel, set: setSel, stockGroup, ...refinementFilters(refinements) }),
    [gameSel, setSel, stockGroup, refinements],
  );
  const {
    data,
    isLoading,
    isLoadingRest,
    error,
    refetch,
    loadMore,
    serverPriced,
    boundsOnClient,
    serverFacets,
    pricedAsOf,
    pricesUpdated,
    refreshPrices,
  } = usePublicBrowse(eventId ?? null, search, sort, chosen);
  // A price range is ignored at a table that shows no prices (the sheet hides it), so a stale link
  // cannot empty the list over prices nobody can see.
  const priceShown = data?.price_visibility !== "none";
  const filters = useMemo<BrowseFilters>(
    () => (priceShown ? chosen : { ...chosen, minCents: null, maxCents: null }),
    [chosen, priceShown],
  );
  // A table QR scan cold-launches into this route, like Book. `isLoading` covers the first page;
  // `isLoadingRest` fills in behind a page the customer can already use.
  useMarkInteractive(!isLoading);
  const { games, sets, stockGroups } = usePublicBrowseGames(eventId ?? null, search);
  const [layout, setLayout] = usePublicBrowseView("list");
  const [introDismissed, introLoaded, dismissIntro] = useBrowseIntroDismissed();

  // Picking (#342): `usePullSheet` owns resumability, the running count and subtotal, and the
  // pick mutations; per-item picked state comes from it, not local state.
  const pullSheet = usePullSheet(eventId ?? null, {
    showPublicId: data?.show_public_id ?? undefined,
    participationId: data?.participation_id ?? undefined,
  });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sellToUsOpen, setSellToUsOpen] = useState(false);
  const tableBuylist = useTableBuylist(
    sellToUsOpen && data?.buylist_published ? data.shop_slug : null,
  );
  const tableQrTracked = useRef(false);
  const buylistTracked = useRef(false);

  useEffect(() => {
    if (!sellToUsOpen || !tableBuylist.data?.page || !data?.shop_slug || buylistTracked.current) {
      return;
    }
    buylistTracked.current = true;
    void loadShopAnalytics().then((analytics) => analytics.buylistViewed(data.shop_slug!, "qr"));
  }, [sellToUsOpen, tableBuylist.data, data?.shop_slug]);

  useEffect(() => {
    if (!data || !eventId || tableQrTracked.current) return;
    tableQrTracked.current = true;
    // Read the arrival before awaiting the analytics load (TRO-644). This screen mirrors search
    // and facets into the URL, so a later read could miss the landing URL's click id.
    const arrival = currentBrowseArrival();
    // Pass the Show when the payload has it: the identity is one site-wide entry (ADR-0106), so a
    // table visit without a Show would adopt the last Show the events host wrote. `showEndsAt`
    // stays null because an Event's browse page stays reachable after the Show and must not clear
    // the identity.
    void loadShowAnalytics({ showId: data.show_public_id ?? null, showEndsAt: null }).then(
      (analytics) => {
        analytics.tableQrOpened(eventId, arrival);
      },
    );
  }, [data, eventId]);

  // The query is kept in the URL like the facets, using the debounced value: each web `setParams`
  // is a `history.replaceState`, which Safari throttles to 100 per 30 seconds and then throws a
  // SecurityError. `openItem` flushes the pending value so a tap-through within the debounce window
  // keeps what was typed.
  useEffect(() => {
    router.setParams({ q: search || undefined });
  }, [search]);
  // The sheet's facets also feed the applied chips beside the sort, so a chip and its row agree.
  const facets = useMemo(
    () =>
      storefrontFacets({
        games,
        sets,
        game: gameSel,
        set: setSel,
        refinements,
        showPrice: priceShown,
      }),
    [games, sets, gameSel, setSel, refinements, priceShown],
  );
  const changeSort = (next: BrowseSort) => {
    setSort(next);
    router.setParams({ sort: next === "featured" ? undefined : next });
  };

  // The running count is copies picked, the sum of quantities (#646), matching what the customer
  // hands the vendor.
  const count = pullSheet.sheet?.items.reduce((n, i) => n + i.quantity, 0) ?? 0;
  const subtotal = pullSheet.sheet?.subtotal ?? "0";

  const insets = useSafeAreaInsets();
  const { wide, maxContentWidth, horizontalPadding, gap, columns, cellWidth } = useStorefrontGrid();
  const grid = layout === "grid";
  const gutters = gridGutters({
    hPad: horizontalPadding,
    vPad: 0,
    colGap: grid ? gap : 0,
    rowGap: grid ? gap : 0,
  });

  // The pages loaded so far for the current server-side query, filter and order.
  const allItems = useMemo(() => data?.items ?? [], [data]);
  // The server orders by default and name, and by price where it reports `server_price_sort`;
  // every facet and any other price order run here.
  const items = useMemo(
    () => filterAndSortBrowse(allItems, sort, filters, { serverOrdered: serverPriced }),
    [allItems, sort, filters, serverPriced],
  );
  const listRef = useRef<ListRef<PublicBrowseItem>>(null);
  // Whether the data on screen answers a search. Uses the debounced `search`, which `data` always
  // matches; using `query` would briefly show the empty-table copy after clearing a zero-match
  // search.
  const searching = search.trim().length > 0;
  // The header shows "N of total". The server counts its own facets (TRO-1856, and game and set
  // where it lists them, TRO-2347); a client-only facet (game and set from an older server, and
  // price where the server did not take the bounds) narrows rows the server counted, so its count
  // is the rows left after the whole table has loaded.
  const filtered = hasBrowseFilters(filters);
  const clientCounted = needsWholeTable(filters, serverFacets)
    || (boundsOnClient && hasPriceBounds(filters));
  const matched = clientCounted ? items.length : data?.total ?? items.length;
  const tableTotal = data?.table_total ?? data?.total ?? 0;
  // Whether the (searched) table has stock at all. Not `allItems`: a server facet that matches
  // nothing returns no rows from a stocked table, which needs "no match", not "nothing here".
  const stocked = tableTotal > 0 || allItems.length > 0;
  // True while typed text has not been debounced and sent yet, so the screen never says "no cards
  // match" for an unsent query.
  const busy = isLoadingRest || query.trim() !== search.trim();

  // The stall's identity scrolls away while search and filters stay pinned in reach (TRO-905).
  const collapse = useCollapsingHeader(insets.top);
  const headerH = collapse.headerHeight;
  const listShown = items.length > 0 || busy;
  // A remounted list (layout or column change) or an empty state starts at the top, so the
  // identity comes back with it.
  const { reset } = collapse;
  useEffect(reset, [reset, grid, columns, listShown]);

  if (isLoading) {
    return <PublicBrowseSkeleton layout={layout} />;
  }

  if (error) {
    return (
      <StorefrontLoadError
        title="Couldn't load this table"
        subtitle={error.message}
        onRetry={() => refetch()}
      />
    );
  }

  const openItem = (itemPublicId: string) => {
    if (query !== search) router.setParams({ q: query || undefined });
    router.push(`/browse/${eventId}/${itemPublicId}`);
  };

  return (
    <StorefrontListShell
      testID="browse-screen"
      headerTestID="browse-header"
      maxContentWidth={maxContentWidth}
      collapse={collapse}
      header={
        <View className="bg-bg px-4 pb-2" style={{ paddingTop: insets.top + 12 }}>
          <View {...collapse.collapsible}>
            {
              /* Orient a customer who just scanned a table QR: whose stall, at what show, and which
             kind of price the rows show. */
            }
            {data
              ? (
                <View className="mb-3 flex-row items-center gap-3">
                  {
                    /* The shop's logo, or a monogram placeholder, leads the storefront. */
                  }
                  <ShopLogo
                    name={data.shop_name}
                    uri={data.shop_logo_url}
                    size={wide ? 64 : 40}
                  />
                  <View className="flex-1">
                    {
                      /* The shop name is the page heading, so screen readers get a document outline
                    (#531). */
                    }
                    <Text
                      className="text-xl font-title text-foreground"
                      accessibilityRole="header"
                      // react-native-web renders an unleveled `header` as `<h1>`, so public-page
                      // headings state their level.
                      aria-level={1}
                      numberOfLines={1}
                    >
                      {data.shop_name}
                    </Text>
                    {
                      /* Show, price mode and count share one line to save height. The count is a
                     separate Text outside the truncating slot, because on a phone one truncated
                     line would cut off the price-mode label the row prices depend on. */
                    }
                    <View className="flex-row items-baseline gap-2">
                      <Text
                        className="min-w-0 flex-1 text-sm font-body text-muted-foreground"
                        numberOfLines={1}
                      >
                        {data.event_name}
                        {data.price_visibility === "ask"
                          ? " · asking prices"
                          : data.price_visibility === "market"
                          ? " · market prices"
                          : ""}
                      </Text>
                      <Text className="shrink-0 text-sm font-body text-muted-foreground">
                        {
                          // A filtered count while later pages still load would undercount, so
                          // it waits for the whole table, as the Filters sheet's action does.
                          filtered && isLoadingRest
                            ? "Counting products…"
                            : filtered
                            ? `${matched.toLocaleString()} of ${tableTotal.toLocaleString()} products`
                            : `${data.total.toLocaleString()} ${
                              data.total === 1 ? "product" : "products"
                            }`
                        }
                      </Text>
                    </View>
                  </View>
                </View>
              )
              : null}
            {
              /* The way up to the vendor's whole shop (ADR-0138). Shown only once the payload names
             a published storefront: a vendor with no shop has nowhere to send them. `push`, not
             `replace`, because Back from the shop belongs at the table the customer is at. */
            }
            {data?.shop_slug
              ? (
                <View className="-mt-1 mb-1">
                  <WholeShopLink onPress={() => router.push(shopPath(data.shop_slug!))} />
                </View>
              )
              : null}
            {data?.buylist_published
              ? (
                <View className="mb-3 flex-row gap-2" accessibilityRole="tablist">
                  <Button
                    title="Pull Sheet"
                    size="sm"
                    variant={sellToUsOpen ? "outline" : "primary"}
                    accessibilityRole="tab"
                    onPress={() => setSellToUsOpen(false)}
                  />
                  <Button
                    title="Sell to us"
                    size="sm"
                    variant={sellToUsOpen ? "primary" : "outline"}
                    accessibilityRole="tab"
                    onPress={() => setSellToUsOpen(true)}
                  />
                </View>
              )
              : null}
            {
              /* Which of the vendor's live tables this is (ADR-0012). `replace`, unlike the tab
             above: crossing between two tables should not stack Back entries the customer has to
             walk out through. */
            }
            {eventId && (data?.live_tables?.length ?? 0) > 1
              ? (
                <View className="mb-3">
                  <TableSwitcher
                    tables={data?.live_tables ?? []}
                    current={eventId}
                    onChange={(next) =>
                      router.replace(
                        data?.shop_slug ? tablePath(data.shop_slug, next) : `/browse/${next}`,
                      )}
                  />
                </View>
              )
              : null}
            {
              /* One line explaining the page for walk-up buyers (#466), above the controls. It sits
             outside the stock branch so an unstocked table still explains itself, and waits for the
             persisted dismissal to load so a returning customer never sees it flash. */
            }
            {introLoaded && !introDismissed
              ? (
                <View className="mb-2">
                  <BrowseIntro
                    stocked={stocked || searching}
                    onDismiss={dismissIntro}
                  />
                </View>
              )
              : null}
          </View>
          {
            /* Search, filter and sort (#468, #768) live in this fixed header so they stay in reach
             over the scrolling list; both public storefronts follow this rule (TRO-905). Shown when
             the table has stock or a search is active, so a zero-match query keeps its box for
             correcting a typo. */
          }
          {!sellToUsOpen && (stocked || searching)
            ? (
              <StorefrontToolbar
                query={query}
                onQueryChange={setQuery}
                filters={{
                  facets,
                  onChange: applyFacetPatch,
                  refinements,
                  showPrice: priceShown,
                  // None while pages are still arriving.
                  resultCount: busy ? undefined : filtered ? matched : data?.total ?? items.length,
                  // The Location is its own pill row here, so Clear all leaves it.
                  onClearAll: () => {
                    storefrontFilters.setGame(null);
                    storefrontFilters.setSet(null);
                    storefrontFilters.setRefinements(NO_REFINEMENTS);
                  },
                }}
                layout={layout}
                onLayoutChange={setLayout}
              >
                {
                  /* The shared filter toolbar (inventory, labels): sort and a removable chip per
                   applied filter, in one row that scrolls sideways, with the Location filter
                   (TRO-1095) in it only when the table spans more than one. A selected Location
                   stays offered even if a search leaves it no stock, so the customer can still
                   clear it here. */
                }
                <BrowseLocationPills
                  locations={stockGroups.length > 1 || stockGroup != null
                    ? stockGroup && !stockGroups.includes(stockGroup)
                      ? [...stockGroups, stockGroup]
                      : stockGroups
                    : []}
                  selected={stockGroup}
                  onChange={storefrontFilters.setStockGroup}
                  sort={<BrowseSortControl sort={sort} onChange={changeSort} />}
                  chips={<AppliedFilterChips facets={facets} onChange={applyFacetPatch} />}
                />
              </StorefrontToolbar>
            )
            : !sellToUsOpen
            ? <BrowseViewToggle layout={layout} onChange={setLayout} />
            : null}
        </View>
      }
    >
      {sellToUsOpen
        ? tableBuylist.isPending
          ? <ActivityIndicator accessibilityLabel="Loading Buylist" />
          : tableBuylist.data?.page
          ? (
            <FlashList
              data={tableBuylist.data.page.cards}
              keyExtractor={(card) => card.public_id}
              contentContainerStyle={{
                paddingHorizontal: 16,
                paddingTop: headerH,
                paddingBottom: 32,
              }}
              ListHeaderComponent={
                <View className="gap-2 pb-4">
                  <Text className="font-title text-2xl text-foreground">We're buying</Text>
                  {tableBuylist.data.page.public_note
                    ? (
                      <Text className="font-body text-foreground">
                        {tableBuylist.data.page.public_note}
                      </Text>
                    )
                    : null}
                  <Text className="font-body text-sm text-muted-foreground">
                    Near Mint prices. Final price confirmed at the table.
                  </Text>
                  <PublicBuylistRules rules={tableBuylist.data.page.rules} />
                  {tableBuylist.data.savedAt
                    ? (
                      <Text className="font-body text-sm text-warning">
                        Saved as of {new Date(tableBuylist.data.savedAt).toLocaleString()}
                      </Text>
                    )
                    : null}
                </View>
              }
              renderItem={({ item }) => <PublicBuylistCardRow card={item} />}
            />
          )
          : (
            <View style={{ paddingTop: headerH }}>
              <EmptyState
                title="Buylist unavailable"
                subtitle="Check your connection and try again."
              />
              <Button title="Try again" onPress={() => void tableBuylist.refetch()} />
            </View>
          )
        : !listShown
        ? !stocked
          ? searching
            ? (
              // The server searched the whole table and matched nothing (#768). Clearing returns
              // to the full list.
              <View
                className="flex-1"
                style={{ paddingTop: headerH }}
                accessibilityLiveRegion="polite"
                testID="browse-no-matches"
              >
                <EmptyState
                  mark
                  title="No cards match your search"
                  subtitle={`Nothing at this table matches “${search.trim()}”. Try a different name or card number.`}
                  action={
                    <Button
                      title="Clear search"
                      accessibilityLabel="Clear search"
                      variant="outline"
                      onPress={() => setQuery("")}
                    />
                  }
                />
              </View>
            )
            : (
              <View
                className="flex-1"
                style={{ paddingTop: headerH }}
                accessibilityLiveRegion="polite"
                testID="browse-empty"
              >
                <EmptyState
                  mark
                  title="Nothing to browse yet"
                  subtitle="The Vendor is still setting up their table — check back soon."
                />
              </View>
            )
          : (
            // The server returned rows, so the filters emptied the list. One tap clears them all.
            <View
              className="flex-1"
              style={{ paddingTop: headerH }}
              accessibilityLiveRegion="polite"
              testID="browse-no-matches"
            >
              <EmptyState
                mark
                title="No products match these filters"
                subtitle="Try another filter, or clear the filters to see everything."
                action={
                  <Button
                    title="Clear filters"
                    accessibilityLabel="Clear filters"
                    variant="outline"
                    onPress={storefrontFilters.clear}
                  />
                }
              />
            </View>
          )
        : (
          <FlashList
            ref={listRef}
            testID="browse-list"
            // `numColumns` cannot change on a mounted list, so remount when the layout or column
            // count changes, as the inventory screen does.
            key={grid ? `grid-${columns}` : "list"}
            data={items}
            // FlashList v2 keeps the first visible row in place when `data` changes, which suits
            // a chat, not a filter: clearing a Location left the list opened at that Location's
            // first row with every row above it scrolled out of view (TRO-1095, iOS).
            maintainVisibleContentPosition={{ disabled: true }}
            {...collapse.list}
            keyExtractor={(item) => item.public_id}
            onEndReached={loadMore}
            onEndReachedThreshold={0.5}
            numColumns={grid ? columns : 1}
            ListHeaderComponent={pricesUpdated
              ? undefined
              : (
                <View style={{ paddingHorizontal: gutters.cellHPad }}>
                  <PricesAsOfLine pricedAsOf={pricedAsOf} />
                </View>
              )}
            contentContainerStyle={{
              paddingHorizontal: gutters.contentHPad,
              paddingTop: headerH + gutters.cellVPad,
              paddingBottom: count > 0 ? insets.bottom + 88 : 24,
            }}
            // A large table loads in pages (#643) and a new search takes a round trip (#768);
            // meanwhile the footer shows more is coming, so the list does not read as empty.
            ListFooterComponent={busy
              ? (
                <View className="items-center gap-1 py-4" accessibilityLiveRegion="polite">
                  <ActivityIndicator />
                  <Text className="text-xs font-body text-muted-foreground">
                    {searching ? "Searching this table…" : "Loading the rest of the table…"}
                  </Text>
                </View>
              )
              : null}
            // The cell draws both gutters, not `columnWrapperStyle` or a container `gap` (see
            // `gridGutters`).
            renderItem={({ item }) => (
              <View
                style={{
                  paddingHorizontal: gutters.cellHPad,
                  paddingVertical: gutters.cellVPad,
                }}
              >
                {grid
                  ? (
                    <PublicGridCard
                      item={item}
                      width={cellWidth}
                      onPress={() => openItem(item.public_id)}
                      quantity={pullSheet.pickedQty(item.public_id)}
                      onSetQuantity={(n) => pullSheet.setPickQuantity(item.public_id, n)}
                      pickPending={pullSheet.isPickPending(item.public_id)}
                      pickLockedReason={pullSheet.pickLockedReason}
                    />
                  )
                  : (
                    <PublicListRow
                      item={item}
                      onPress={() => openItem(item.public_id)}
                      quantity={pullSheet.pickedQty(item.public_id)}
                      onSetQuantity={(n) => pullSheet.setPickQuantity(item.public_id, n)}
                      pickPending={pullSheet.isPickPending(item.public_id)}
                      pickLockedReason={pullSheet.pickLockedReason}
                    />
                  )}
              </View>
            )}
          />
        )}

      {!sellToUsOpen
        ? (
          <PullSheetBar
            count={count}
            subtotal={subtotal}
            phase={data?.event_phase ?? "live"}
            // After check-out the bar reads "#N in line" and opens the drawer's waiting view instead
            // of the editable list (TRO-673).
            inLine={pullSheet.isPresented}
            queuePosition={pullSheet.sheet?.queue_position ?? null}
            queueTotal={pullSheet.sheet?.queue_total ?? null}
            onPress={() => {
              // Clear any list-tap error; the review modal shows its own errors inline.
              pullSheet.clearActionError();
              setReviewOpen(true);
            }}
          />
        )
        : null}
      {
        /* Quick-pick errors show here while the modal is closed; the modal shows its own errors
            inline, so this hides while it is open. */
      }
      {pricesUpdated && !sellToUsOpen
        ? (
          <PricesUpdatedOverlay
            headerHeight={headerH}
            headerStyle={collapse.header.style}
            onPress={() => {
              refreshPrices();
              listRef.current?.scrollToOffset({ offset: 0, animated: true });
            }}
          />
        )
        : null}
      <PullSheetErrorToast
        message={reviewOpen ? null : pullSheet.actionError}
        onDismiss={pullSheet.clearActionError}
      />
      <PullSheetReviewModal
        // The Vendor settles, not the customer, so the drawer opens itself to confirm it; the bar
        // that would open it disappears with the settled sheet.
        visible={reviewOpen || pullSheet.justSettled}
        phase={data?.event_phase ?? "live"}
        onClose={() => {
          setReviewOpen(false);
          pullSheet.clearActionError();
          // Reset the withdraw success flag with the modal (TRO-674), so a new list does not show
          // the old "you're out of the line" message.
          pullSheet.withdraw.reset();
          pullSheet.acknowledgeSettled();
        }}
        justSettled={pullSheet.justSettled}
        shopName={data?.shop_name}
        sheet={pullSheet.sheet}
        onSetQuantity={(stockItemPublicId, quantity) =>
          pullSheet.setPickQuantity(stockItemPublicId, quantity)}
        isPending={pullSheet.isPickPending}
        onPresent={(customerLabel) => pullSheet.present.mutate(customerLabel)}
        isPresenting={pullSheet.present.isPending}
        onWithdraw={() => pullSheet.withdraw.mutate()}
        isWithdrawing={pullSheet.withdraw.isPending}
        justWithdrew={pullSheet.withdraw.isSuccess}
        error={pullSheet.actionError}
      />
    </StorefrontListShell>
  );
}
